/**
 * BlogPage — /blog
 * ═════════════════
 * Central hub linking every article, guide, and help topic.
 * Organised into four clear sections:
 *   1. Help Centre (FAQ-style for mentees)
 *   2. For Learners (guides: Find Mentor, Get Certified, etc.)
 *   3. For Mentors (handbook, payout, apply)
 *   4. Platform News & Training Articles
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useSEO } from '../utils/seo';
import './BlogPage.css';

/* ── All content organised for the hub ─────────────────────── */
const HELP_TOPICS = [
  {
    slug: 'how-to-find-a-mentor',
    emoji: '🔍',
    title: 'How to Find the Right Mentor',
    desc: 'Step-by-step guide to searching, filtering, and booking the perfect mentor for your goals.',
    tag: 'Getting Started',
    tagColor: '#16a34a',
  },
  {
    slug: 'how-payment-works',
    emoji: '💳',
    title: 'How Payment Works',
    desc: 'From booking to invoice — how Razorpay escrow, accepted methods, and GST billing work.',
    tag: 'Payments',
    tagColor: '#2563eb',
  },
  {
    slug: 'cancellation-policy',
    emoji: '🔄',
    title: 'Cancellation & Refund Policy',
    desc: 'When you get a full refund, a partial refund, or no refund — and how to reschedule for free.',
    tag: 'Payments',
    tagColor: '#2563eb',
  },
  {
    slug: 'mentor-did-not-join',
    emoji: '⚠️',
    title: 'What If My Mentor Doesn\'t Join?',
    desc: 'Your automatic protections, the 15-minute rule, and how to trigger a full refund.',
    tag: 'Session Help',
    tagColor: '#d97706',
  },
  {
    slug: 'mentor-missed-meeting',
    emoji: '🚨',
    title: 'Mentor Missed the Meeting — What Now?',
    desc: 'Exactly what happens to your payment, your time, and the mentor\'s account when they no-show.',
    tag: 'Session Help',
    tagColor: '#d97706',
  },
  {
    slug: 'why-devbrains-is-trusted',
    emoji: '🛡️',
    title: 'Why DevBrains is Trusted',
    desc: 'Verified mentors, escrow payments, honest reviews, blockchain certificates — how we earn your trust.',
    tag: 'Trust & Safety',
    tagColor: '#7c3aed',
  },
  {
    slug: 'how-mentors-get-ratings',
    emoji: '⭐',
    title: 'How Ratings & Testimonials Work',
    desc: 'How sessions trigger the review flow, who can leave ratings, and how group session reviews work.',
    tag: 'Reviews',
    tagColor: '#0891b2',
  },
  {
    slug: 'how-to-become-a-mentor',
    emoji: '🧑‍🏫',
    title: 'How to Become a Mentor',
    desc: 'Who qualifies, the 4-step application process, and what to expect after you apply.',
    tag: 'For Mentors',
    tagColor: '#dc2626',
  },
  {
    slug: 'mentor-approval-process',
    emoji: '✅',
    title: 'The Mentor Approval Process',
    desc: 'A transparent look at all 4 verification stages, timeline, and approval rates.',
    tag: 'For Mentors',
    tagColor: '#dc2626',
  },
];

const LEARNER_GUIDES = [
  {
    slug: 'find-a-mentor-guide',
    emoji: '🔍',
    title: 'Find a Mentor',
    desc: 'Search 2,400+ verified engineers. Filter by skill, price, rating, and availability.',
    cta: 'Read Guide',
  },
  {
    slug: 'get-certified-guide',
    emoji: '🏆',
    title: 'Get Certified',
    desc: 'Earn project-based certificates that employers trust — built, reviewed, and defended.',
    cta: 'Read Guide',
  },
  {
    slug: 'browse-by-tech',
    emoji: '💻',
    title: 'Browse by Tech',
    desc: 'Find specialists by exact technology — React, Python, AWS, Flutter, and 50+ more.',
    cta: 'Read Guide',
  },
  {
    slug: 'group-sessions-guide',
    emoji: '👥',
    title: 'Group Sessions',
    desc: 'Live workshops with top mentors at 10–20% of the cost of a 1:1 session.',
    cta: 'Read Guide',
  },
  {
    slug: 'career-roadmap-guide',
    emoji: '🗺️',
    title: 'Career Roadmap',
    desc: 'Plan your path from where you are to your dream job with a personalised mentor-built roadmap.',
    cta: 'Read Guide',
  },
];

const MENTOR_GUIDES = [
  {
    slug: 'how-to-become-a-mentor',
    emoji: '🚀',
    title: 'Apply Now',
    desc: 'Join 2,400+ verified mentors. Applications reviewed within 5 business days.',
    cta: 'Apply',
    primary: true,
    externalLink: '/become-mentor',
  },
  {
    slug: 'mentor-handbook',
    emoji: '📖',
    title: 'Mentor Handbook',
    desc: 'Everything you need to run great sessions, build your reputation, and grow income.',
    cta: 'Read Handbook',
  },
  {
    slug: 'mentor-payout-info',
    emoji: '💰',
    title: 'Payout Info',
    desc: 'How the 85/15 split works, withdrawal methods, TDS certificates, and GST guidance.',
    cta: 'Read Guide',
  },
  {
    slug: 'how-mentors-get-ratings',
    emoji: '⭐',
    title: 'Getting Ratings & Testimonials',
    desc: 'Best practices to earn 5-star reviews, build your profile, and stand out in search.',
    cta: 'Read Guide',
  },
  {
    slug: 'mentor-approval-process',
    emoji: '✅',
    title: 'Approval Process',
    desc: 'What we check at each of the 4 verification stages, and how to ace the screening interview.',
    cta: 'Read Guide',
  },
];

const TRAINING_HIGHLIGHTS = [
  { slug: 'prepare-for-first-session',    emoji: '🎯', title: 'How to Prepare for Your First Session',       tag: 'Onboarding' },
  { slug: 'code-review-best-practices',   emoji: '🔍', title: 'Getting Maximum Value from Code Reviews',      tag: 'Best Practices' },
  { slug: 'mock-interview-guide',         emoji: '🎤', title: 'Complete Guide to Mock Technical Interviews',  tag: 'Interview Prep' },
  { slug: 'building-your-dev-portfolio',  emoji: '💼', title: 'Building a Portfolio That Gets You Hired',     tag: 'Career Growth' },
  { slug: 'certification-program-guide',  emoji: '🏆', title: 'How the Certification Program Works',         tag: 'Certification' },
];

/* ── Card components ────────────────────────────────────────── */
function HelpCard({ item }) {
  return (
    <Link to={`/blog/article/${item.slug}`} className="bh-help-card">
      <div className="bh-help-card-top">
        <span className="bh-help-emoji">{item.emoji}</span>
        <span className="bh-help-tag" style={{ color: item.tagColor, background: `${item.tagColor}15` }}>
          {item.tag}
        </span>
      </div>
      <h3 className="bh-help-title">{item.title}</h3>
      <p className="bh-help-desc">{item.desc}</p>
      <span className="bh-help-cta">Read more →</span>
    </Link>
  );
}

function GuideCard({ item }) {
  return (
    <Link
      to={item.externalLink || `/blog/article/${item.slug}`}
      className={`bh-guide-card ${item.primary ? 'primary' : ''}`}
    >
      <span className="bh-guide-emoji">{item.emoji}</span>
      <div className="bh-guide-info">
        <div className="bh-guide-title">{item.title}</div>
        <div className="bh-guide-desc">{item.desc}</div>
      </div>
      <span className="bh-guide-arrow">→</span>
    </Link>
  );
}

function TrainingCard({ item }) {
  return (
    <Link to={`/training/${item.slug}`} className="bh-training-card">
      <span className="bh-training-emoji">{item.emoji}</span>
      <div>
        <div className="bh-training-title">{item.title}</div>
        <span className="bh-training-tag">{item.tag}</span>
      </div>
      <span className="bh-training-arrow">→</span>
    </Link>
  );
}

/* ── Main Page ──────────────────────────────────────────────── */
export default function BlogPage({ onSignIn }) {
  const [search, setSearch] = useState('');

  useSEO({
    title: 'Help Centre & Blog',
    description: 'Find answers to all your DevBrains questions — how to book sessions, payment help, cancellations, mentor guides, and platform news.',
    canonical: '/blog',
  });

  const filteredHelp = search
    ? HELP_TOPICS.filter(t =>
        t.title.toLowerCase().includes(search.toLowerCase()) ||
        t.desc.toLowerCase().includes(search.toLowerCase())
      )
    : HELP_TOPICS;

  return (
    <>
      <Navbar onSignIn={onSignIn} />

      <div className="bh-page">

        {/* ── Hero ──────────────────────────────────────────── */}
        <div className="bh-hero">
          <div className="bh-hero-inner">
            <div className="bh-hero-badge">💡 Help Centre &amp; Blog</div>
            <h1 className="bh-hero-title">How can we help you?</h1>
            <p className="bh-hero-sub">
              Find answers to common questions, read platform guides,
              or explore mentoring best practices.
            </p>

            {/* Search */}
            <div className="bh-search-wrap">
              <span className="bh-search-icon">🔍</span>
              <input
                className="bh-search"
                placeholder="Search articles — e.g. 'refund', 'cancel', 'payment'..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="bh-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>

            {/* Quick jump chips */}
            {!search && (
              <div className="bh-hero-chips">
                {['Help Centre', 'For Learners', 'For Mentors', 'Training Articles'].map(s => (
                  <a key={s} href={`#${s.toLowerCase().replace(/ /g,'-')}`} className="bh-hero-chip">{s}</a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bh-content">

          {/* ── Search Results ──────────────────────────────── */}
          {search && (
            <section className="bh-section">
              <div className="bh-section-head">
                <h2 className="bh-section-title">Search Results</h2>
                <span className="bh-section-count">{filteredHelp.length} found</span>
              </div>
              {filteredHelp.length === 0 ? (
                <div className="bh-empty">
                  <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>No articles found</div>
                  <div style={{ color: '#9ca3af', fontSize: '.84rem' }}>Try different keywords or browse sections below</div>
                  <button onClick={() => setSearch('')} className="bh-empty-clear">Clear Search</button>
                </div>
              ) : (
                <div className="bh-help-grid">{filteredHelp.map(t => <HelpCard key={t.slug} item={t} />)}</div>
              )}
            </section>
          )}

          {/* ── Help Centre ─────────────────────────────────── */}
          {!search && (
            <section className="bh-section" id="help-centre">
              <div className="bh-section-head">
                <div>
                  <h2 className="bh-section-title">❓ Help Centre</h2>
                  <p className="bh-section-sub">Answers to the most common questions from mentees and mentors.</p>
                </div>
              </div>
              <div className="bh-help-grid">
                {HELP_TOPICS.map(t => <HelpCard key={t.slug} item={t} />)}
              </div>
            </section>
          )}

          {/* ── For Learners ────────────────────────────────── */}
          {!search && (
            <section className="bh-section" id="for-learners">
              <div className="bh-section-head">
                <div>
                  <h2 className="bh-section-title">📚 For Learners</h2>
                  <p className="bh-section-sub">Step-by-step guides for mentees — from finding your first mentor to earning a certificate.</p>
                </div>
                <Link to="/mentors" className="bh-section-cta">Browse Mentors →</Link>
              </div>
              <div className="bh-guide-list">
                {LEARNER_GUIDES.map(g => <GuideCard key={g.slug} item={g} />)}
              </div>
            </section>
          )}

          {/* ── For Mentors ─────────────────────────────────── */}
          {!search && (
            <section className="bh-section mentor-section" id="for-mentors">
              <div className="bh-mentor-inner">
                <div className="bh-section-head">
                  <div>
                    <h2 className="bh-section-title" style={{ color: '#fff' }}>🧑‍🏫 For Mentors</h2>
                    <p className="bh-section-sub" style={{ color: 'rgba(255,255,255,.6)' }}>
                      Everything you need to apply, get verified, and grow your mentoring income.
                    </p>
                  </div>
                  <Link to="/become-mentor" className="bh-section-cta mentor-cta">Apply Now →</Link>
                </div>

                {/* Earnings highlight */}
                <div className="bh-mentor-stats">
                  {[
                    { n: '₹50k–₹1.5L', l: 'Monthly earnings (top mentors)' },
                    { n: '85%', l: 'Revenue share (you keep)' },
                    { n: '5–10 days', l: 'Application to first booking' },
                  ].map(s => (
                    <div key={s.l} className="bh-mentor-stat">
                      <strong>{s.n}</strong>
                      <span>{s.l}</span>
                    </div>
                  ))}
                </div>

                <div className="bh-guide-list">
                  {MENTOR_GUIDES.map(g => <GuideCard key={g.slug} item={g} />)}
                </div>
              </div>
            </section>
          )}

          {/* ── Training Articles ───────────────────────────── */}
          {!search && (
            <section className="bh-section" id="training-articles">
              <div className="bh-section-head">
                <div>
                  <h2 className="bh-section-title">🎓 Training Articles</h2>
                  <p className="bh-section-sub">Best practices, interview prep, and career guides from the DevBrains team.</p>
                </div>
                <Link to="/training" className="bh-section-cta">All Articles →</Link>
              </div>
              <div className="bh-training-list">
                {TRAINING_HIGHLIGHTS.map(t => <TrainingCard key={t.slug} item={t} />)}
              </div>
            </section>
          )}

          {/* ── Legal Links ─────────────────────────────────── */}
          {!search && (
            <section className="bh-legal-row">
              <Link to="/terms" className="bh-legal-card">
                <span className="bh-legal-icon">📋</span>
                <div>
                  <div className="bh-legal-title">Terms &amp; Conditions</div>
                  <div className="bh-legal-sub">Your rights and obligations as a DevBrains user</div>
                </div>
                <span className="bh-legal-arrow">→</span>
              </Link>
              <Link to="/privacy" className="bh-legal-card">
                <span className="bh-legal-icon">🔒</span>
                <div>
                  <div className="bh-legal-title">Privacy Policy</div>
                  <div className="bh-legal-sub">How we collect, use, and protect your data</div>
                </div>
                <span className="bh-legal-arrow">→</span>
              </Link>
              <Link to="/news" className="bh-legal-card">
                <span className="bh-legal-icon">📢</span>
                <div>
                  <div className="bh-legal-title">Platform News</div>
                  <div className="bh-legal-sub">Latest features, updates and maintenance notices</div>
                </div>
                <span className="bh-legal-arrow">→</span>
              </Link>
            </section>
          )}

          {/* ── Still need help CTA ─────────────────────────── */}
          <div className="bh-contact-cta">
            <div className="bh-contact-icon">💬</div>
            <h3>Still have a question?</h3>
            <p>Our support team is available Monday to Saturday, 9 AM to 9 PM IST. We typically reply within 2 hours.</p>
            <div className="bh-contact-actions">
              <a href="mailto:support@devbrains.in" className="bh-contact-btn primary">
                ✉️ Email Support
              </a>
              <a href="https://wa.me/919999999999" target="_blank" rel="noopener noreferrer" className="bh-contact-btn whatsapp">
                💬 WhatsApp
              </a>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
